import { useThreadDiscovery, useThreadDiscoveryPins } from "../threadDiscoveryContext";
import { useCallback, useRef } from "react";
import type { ScopedThreadRef } from "@afar-research/contracts";
import { threadManagementSection, type ThreadManagementAction } from "@afar-research/afar";
import { readThreadShell, useProjects } from "../state/entities";
import { threadEnvironment } from "../state/threads";
import { useAtomCommand } from "../state/use-atom-command";
import { showThreadUndoNotice } from "./showThreadUndoNotice";
import * as ThreadUndo from "./threadUndo";

import { useRouter } from "@tanstack/react-router";
import {
  scopedThreadKey,
  scopeThreadRef,
  scopeProjectRef,
} from "@afar-research/client-runtime/environment";
import { buildThreadRouteParams, resolveThreadRouteRef } from "../threadRoutes";

import { useClientSettings } from "./useSettings";
import { deriveLogicalProjectKeyFromSettings } from "../logicalProject";
import { useNewThreadHandler } from "./useHandleNewThread";

/** All UI entry points dispatch against the current authoritative thread revision. */
export function useThreadManagement() {
  const { unpinThread } = useThreadDiscoveryPins();
  const projects = useProjects();
  const settings = useClientSettings((settings) => settings);
  const newThread = useNewThreadHandler();
  const newThreadRef = useRef(newThread);
  newThreadRef.current = newThread;
  const scopeRefs = projects
    .filter(
      (project) =>
        !settings.sidebarProjectScopeKey ||
        deriveLogicalProjectKeyFromSettings(project, settings) === settings.sidebarProjectScopeKey,
    )
    .map((project) => scopeProjectRef(project.environmentId, project.id));
  const scopeRefsRef = useRef(scopeRefs);
  scopeRefsRef.current = scopeRefs;
  const model = useThreadDiscovery();
  const modelRef = useRef(model);
  modelRef.current = model;
  const router = useRouter();
  const inFlight = useRef(new Set<string>());
  const mutate = useAtomCommand(threadEnvironment.manage);
  return useCallback(
    async (target: ScopedThreadRef, action: ThreadManagementAction, until?: string) => {
      const key = scopedThreadKey(target);
      if (inFlight.current.has(key)) return;
      const thread = readThreadShell(target);
      if (!thread) return;
      const routeRef = () => resolveThreadRouteRef(router.state.matches.at(-1)?.params ?? {});
      const current = routeRef();
      const parking = action === "settle" || action === "snooze";
      const wasOpen = current !== null && scopedThreadKey(current) === key;
      const cards = modelRef.current
        .discover({ groups: ["pinned", "live"], projectRefs: scopeRefsRef.current })
        .items.filter(
          (item) => !item.disabled && threadManagementSection(item.thread, Date.now()) === "active",
        );
      const index = cards.findIndex((item) => item.key === key);
      const remaining =
        index < 0 ? cards : [...cards.slice(index + 1), ...cards.slice(0, index).toReversed()];
      inFlight.current.add(key);
      try {
        const result = await mutate({
          environmentId: target.environmentId,
          input: {
            threadId: target.threadId,
            expectedRevision: thread.updatedAt,
            action,
            ...(until ? { until } : {}),
          },
        });
        if (result._tag !== "Success") return;
        if (action === "settle") unpinThread(target);
        if (parking) {
          // The reverse revision is read at undo time, not captured here: the
          // thread may have moved on in the seconds the notice is up, and an
          // undo against a stale revision would be refused.
          const claim = ThreadUndo.begin("management", key);
          showThreadUndoNotice({
            action: action === "settle" ? "Settled" : "Snoozed",
            failureTitle: action === "settle" ? "Could not un-settle" : "Could not unsnooze",
            claim,
            undo: async () => {
              const latest = readThreadShell(target);
              return await mutate({
                environmentId: target.environmentId,
                input: {
                  threadId: target.threadId,
                  expectedRevision: latest?.updatedAt ?? thread.updatedAt,
                  action: action === "settle" ? "restore" : "unsnooze",
                },
              });
            },
          });
        } else {
          // Restoring or waking by hand settles the question; an Undo still
          // offering to reverse the parking would now be reversing nothing.
          ThreadUndo.invalidate("management", key);
        }
        const stillOpen = routeRef();
        if (parking && wasOpen && stillOpen !== null && scopedThreadKey(stillOpen) === key) {
          const next = remaining.find((item) => {
            const latest = readThreadShell(
              scopeThreadRef(item.thread.environmentId, item.thread.id),
            );
            return (
              latest &&
              latest.archivedAt === null &&
              threadManagementSection(latest, Date.now()) === "active"
            );
          });
          if (next)
            await router.navigate({
              to: "/$environmentId/$threadId",
              params: buildThreadRouteParams(
                scopeThreadRef(next.thread.environmentId, next.thread.id),
              ),
            });
          else await newThreadRef.current(scopeProjectRef(thread.environmentId, thread.projectId));
        }
      } finally {
        inFlight.current.delete(key);
      }
    },
    [mutate, unpinThread, router],
  );
}
